import React, { useEffect, Dispatch, SetStateAction } from "react";
import {
  Button,
  Checkbox,
  Form,
  FormField,
  FormGroup,
  Input,
  Modal,
  ModalContent,
  Segment,
} from "semantic-ui-react";

// Components
import { DEFAULT_DOCTOR_NOTE_TOOLBAR_BTNS } from "./DoctorNoteTextEditor";
import { TemplateItem } from "./CardHealthRecommendation";

// Serializer
import DoctorRecommendationTemplateSerializer from "issara-sdk/types/DoctorRecommendationTemplateSerializer_core";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SunEditor from "suneditor-react";
import { Table } from "react-lib/frameworks/Table";

// Interface
import { SetProp, PickedState, BTN_ACTS } from "./sequence/HealthRecommendation";

// Types
type DoctorNoteTemplatePopupProps = {
  // Common
  setProp: SetProp;
  onOpen: boolean;
  onClose: () => void;
  onSearch: (name: string) => void;
  onCreate: (createdData: DoctorRecommendationTemplateSerializer) => void;
  onEdit: (editedData: DoctorRecommendationTemplateSerializer) => void;
  onDelete: (templateId: number) => void;
  onSelectTemplate: (templates: DoctorRecommendationTemplateSerializer[]) => void;

  // Data
  templates: TemplateItem[];
  selectedTemplates: DoctorRecommendationTemplateSerializer[];
  searchKeyword: string;

  // Setter
  onChangeSearch: (value: string) => void;
  onChangeTemplates: Dispatch<SetStateAction<TemplateItem[]>>;
  onChangeSelectedTemplates: (selected: DoctorRecommendationTemplateSerializer[]) => void;
} & Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "selectedEncounter" | "selectedEmr"
>;

const ModDoctorNoteTemplate = (props: DoctorNoteTemplatePopupProps) => {
  const {
    // Common
    setProp,
    onOpen,
    onClose,
    onSearch,
    onCreate,
    onEdit,
    onDelete,
    onSelectTemplate,

    // Data
    templates,
    // setTemplates,
    selectedTemplates,
    // setSelectedTemplates,
    searchKeyword,

    // Setter
    onChangeSearch,
    onChangeTemplates,
    onChangeSelectedTemplates,

    // Other
    buttonLoadCheck,
  } = props;

  useEffect(() => {
    if (!onOpen) {
      onChangeSearch("");
      onChangeSelectedTemplates([]);
    }
  }, [onOpen]);

  const handleSaveNEditTemplate = (item: TemplateItem) => {
    if (!item.id) {
      onCreate({ name: item.name, detail: item.detail });
    } else {
      const { isEditing, ...editedData } = item;

      onEdit(editedData);
    }
  };

  const handleDeleteTemplate = (item: TemplateItem) => {
    if (item.id) onDelete(item.id);
  };

  const toggleTemplateSelect = (item: TemplateItem, checked: boolean) => {
    if (checked) {
      onChangeSelectedTemplates([...selectedTemplates, item]);
    } else {
      onChangeSelectedTemplates(selectedTemplates.filter((t) => t.id !== item.id));
    }
  };

  const updateTemplate = (id: number | undefined, patch: Partial<TemplateItem>) => {
    onChangeTemplates((prev) =>
      prev.map((template) => (template.id === id ? { ...template, ...patch } : template))
    );
  };

  const handleCancelNewTemplate = (item: TemplateItem) => {
    onChangeTemplates((prev) => prev.filter((t) => t !== item));
  };

  // console.log("Yeti Mod Data: ", props);

  return (
    <Modal open={onOpen} onClose={onClose}>
      <Segment inverted color="blue">
        <label style={{ fontWeight: "bold" }}>Doctor's Recommendation</label>
      </Segment>
      <ModalContent>
        <Form>
          {/* Filter Section */}
          <FormGroup>
            <FormField inline style={{ width: "100%" }}>
              <Input
                style={{ width: "74%" }}
                icon="search"
                placeholder="Search For Template Name"
                value={searchKeyword}
                onChange={(_event) => onChangeSearch(_event.target.value)}
              />
              <ButtonLoadCheck
                // func
                setProp={setProp}
                onClick={() => onSearch(searchKeyword)}
                // data
                paramKey={BTN_ACTS.GET_TEMPLATE}
                buttonLoadCheck={buttonLoadCheck?.[BTN_ACTS.GET_TEMPLATE]}
                // config
                name="Search"
                color="blue"
                title="Search"
                size="medium"
              />
              <Button
                color="yellow"
                onClick={() =>
                  onChangeTemplates([
                    ...templates,
                    {
                      id: undefined,
                      name: "",
                      detail: "",
                      active: true,
                      isEditing: true,
                    },
                  ])
                }
              >
                Create New
              </Button>
            </FormField>
          </FormGroup>
          {/* Table Section */}
          <FormGroup>
            <FormField inline style={{ width: "100%" }}>
              <Table
                data={templates.map((item) => ({
                  _id: (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Checkbox
                        checked={selectedTemplates.some((template) => template.id === item.id)}
                        onChange={(_event, data) => toggleTemplateSelect(item, !!data.checked)}
                      />
                    </div>
                  ),
                  name: item.isEditing ? (
                    <Input
                      key={item.id}
                      style={{ width: "100px" }}
                      value={item.name}
                      disabled={!item.isEditing}
                      onChange={(_event) => updateTemplate(item.id, { name: _event.target.value })}
                    />
                  ) : (
                    <label>{item.name}</label>
                  ),
                  _note: (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          paddingBottom: "1rem",
                        }}
                      >
                        {item.isEditing ? (
                          <ButtonLoadCheck
                            // func
                            setProp={setProp}
                            onClick={() => handleSaveNEditTemplate(item)}
                            // data
                            paramKey={
                              item.id
                                ? `${BTN_ACTS.EDIT_TEMPLATE}_${item.id}`
                                : `${BTN_ACTS.CREATE_TEMPLATE}`
                            }
                            buttonLoadCheck={
                              buttonLoadCheck?.[
                                item.id
                                  ? `${BTN_ACTS.EDIT_TEMPLATE}_${item.id}`
                                  : `${BTN_ACTS.CREATE_TEMPLATE}`
                              ]
                            }
                            // config
                            icon="save outline"
                            color="green"
                            size="mini"
                          />
                        ) : (
                          <Button
                            onClick={() => updateTemplate(item.id, { isEditing: true })}
                            icon="edit"
                            color="yellow"
                            size="mini"
                          />
                        )}
                        {!item.id ? (
                          <Button
                            icon="close"
                            color="grey"
                            size="mini"
                            onClick={() => handleCancelNewTemplate(item)}
                          />
                        ) : (
                          <ButtonLoadCheck
                            // func
                            setProp={setProp}
                            onClick={() => handleDeleteTemplate(item)}
                            // data
                            paramKey={`${BTN_ACTS.DELETE_TEMPLATE}_${item.id}`}
                            buttonLoadCheck={
                              buttonLoadCheck?.[`${BTN_ACTS.DELETE_TEMPLATE}_${item.id}`]
                            }
                            // config
                            icon="trash alternate"
                            color="red"
                            size="mini"
                          />
                        )}
                      </div>
                      <div>
                        <SunEditor
                          key={item.id || "new"}
                          setContents={item.detail || ""}
                          readOnly={!item.isEditing}
                          autoFocus={false}
                          height="100%"
                          onChange={(content) => updateTemplate(item.id, { detail: content })}
                          setOptions={{
                            defaultTag: "div",
                            showPathLabel: false,
                            ...(item.isEditing
                              ? { buttonList: DEFAULT_DOCTOR_NOTE_TOOLBAR_BTNS }
                              : {}),
                            hideToolbar: !item.isEditing,
                          }}
                        />
                      </div>
                    </div>
                  ),
                }))}
                keys="_id, name, _note"
                style={{ height: "calc(100vh - 20rem)" }}
                widths="^20, ^100, ^350"
                minRows={5}
              />
            </FormField>
          </FormGroup>
          {/* Button Section */}
          <FormGroup style={{ display: "flex", justifyContent: "center" }}>
            <FormField inline>
              <Button basic color="red" onClick={onClose}>
                CLOSE
              </Button>
              <Button
                type="button"
                basic
                color="green"
                onClick={() => {
                  onSelectTemplate(selectedTemplates);
                  onClose();
                }}
              >
                CONFIRM
              </Button>
            </FormField>
          </FormGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(ModDoctorNoteTemplate);
